// ═══════════════════════════════════════════════════════════════════════
// Paramètres › Application mobile — versions de SOGAS Employé (migration 0077 ; docs/rh/mobile-employe/MISES_A_JOUR.md).
// Préparer une version à partir d'un APK DÉJÀ DÉPOSÉ sur le serveur (le serveur calcule taille et empreinte), la
// faire valider par une AUTRE personne (double regard) — c'est la validation qui l'active —, ou la retirer.
// Droit : gerer_versions_mobile (portée entreprise), vérifié par le serveur à chaque appel.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Smartphone, Plus, CheckCircle2, Ban, ShieldCheck } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Selecteur } from '../../design/composants';
import { Fenetre, Champ, PiedFenetre, messageErreur } from '../temps/reglagesCommun';
import { dateHeure } from '../temps/rhCommun';

const API = '/api/versions-mobile';
const LIBELLES_CANAL = { recette: 'Recette', officiel: 'Officiel' };
const TONS_STATUT = { BROUILLON: 'alerte', VALIDEE: 'succes', RETIREE: 'neutre' };
const LIBELLES_FICHIER = { PRESENT: null, ABSENT: 'FICHIER ABSENT — non proposé aux téléphones', MODIFIE: 'FICHIER MODIFIÉ — non proposé aux téléphones', INVALIDE: 'Fichier invalide' };
const toArray = (d) => (Array.isArray(d) ? d : (d?.rows || d?.data || []));
const taille = (o) => (o ? `${(Number(o) / 1048576).toFixed(1)} Mo` : '');

function FenetrePreparer({ fichiers, onFermer, onPreparee }) {
  const [f, setF] = useState({ fichier: '', versionName: '', versionCode: '', minVersionCode: '0', obligatoire: false, notes: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const libres = fichiers.filter((x) => !x.deja_enregistre);
  const choisi = libres.find((x) => x.fichier === f.fichier) || null;
  const choisir = (nom) => {
    const x = libres.find((y) => y.fichier === nom);
    const s = x?.version_suggeree;
    setF({ ...f, fichier: nom, versionName: s?.versionName || '', versionCode: s?.versionCode ? String(s.versionCode) : '' });
  };
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try {
      const corps = { canal: choisi.canal, fichier: f.fichier, versionName: f.versionName.trim(), versionCode: Number(f.versionCode),
        minVersionCode: Number(f.minVersionCode || 0), obligatoire: f.obligatoire, notes: f.notes.trim() };
      onPreparee((await axios.post(API, corps)).data.data);
    } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  const complet = choisi && /^\d+\.\d+\.\d+$/.test(f.versionName.trim()) && /^\d+$/.test(f.versionCode) && /^\d+$/.test(f.minVersionCode || '0');
  return (
    <Fenetre titre="Préparer une version" Icone={Plus} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 10px', fontSize: 13 }}>
        Le fichier doit déjà être déposé sur le serveur (dossier de publication, ou « recette/ » pour la recette), avec la version et le code dans son nom.
        Le serveur calcule lui-même sa taille et son empreinte, et lit l'application : identifiant, version, code et clé de signature doivent correspondre.
        La version ne sera proposée aux téléphones qu'après la validation d'une autre personne.
      </p>
      <Champ libelle="Fichier APK" requis aide={choisi ? `Canal ${LIBELLES_CANAL[choisi.canal]} · ${taille(choisi.taille)} · déposé le ${dateHeure(choisi.modifie_le)}` : 'Seuls les fichiers non encore enregistrés sont proposés.'}>
        <select className="rh-champ-saisie" value={f.fichier} onChange={(e) => choisir(e.target.value)}>
          <option value="">Choisir…</option>
          {libres.map((x) => <option key={x.fichier} value={x.fichier}>{x.fichier}</option>)}
        </select>
      </Champ>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <Champ libelle="Version" requis aide="Ex. 1.3.0 (identique au nom du fichier)">
          <input className="rh-champ-saisie" value={f.versionName} maxLength={20} onChange={(e) => setF({ ...f, versionName: e.target.value })} />
        </Champ>
        <Champ libelle="Code de version" requis aide="Nombre après « + » dans pubspec.yaml">
          <input className="rh-champ-saisie" inputMode="numeric" value={f.versionCode} maxLength={10} onChange={(e) => setF({ ...f, versionCode: e.target.value.replace(/\D/g, '') })} />
        </Champ>
      </div>
      <Champ libelle="Version minimale acceptée (code)" aide="Les téléphones dont le code est inférieur seront obligés de mettre à jour. 0 = aucune obligation par ce moyen.">
        <input className="rh-champ-saisie" inputMode="numeric" value={f.minVersionCode} maxLength={10} onChange={(e) => setF({ ...f, minVersionCode: e.target.value.replace(/\D/g, '') })} />
      </Champ>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12 }}>
        <input type="checkbox" checked={f.obligatoire} onChange={(e) => setF({ ...f, obligatoire: e.target.checked })} />
        Mise à jour obligatoire pour tous (sinon : recommandée, le salarié peut répondre « Plus tard »)
      </label>
      <Champ libelle="Notes affichées au salarié" aide={`Phrase courte, sans jargon (${f.notes.length}/500).`}>
        <textarea className="rh-champ-saisie" rows={3} maxLength={500} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Plus} onClick={envoyer} disabled={envoi || !complet}>Préparer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

function FenetreValider({ version, onFermer, onValidee }) {
  const [verifie, setVerifie] = useState(false);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const v = version;
  const valider = async () => {
    setEnvoi(true); setErreur('');
    try { await axios.post(`${API}/${v.id}/valider`); onValidee(); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Fenetre titre={`Valider la version ${v.versionName}`} Icone={ShieldCheck} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <BandeauInfo ton="alerte" titre="La validation active la version">
        Dès la validation, les téléphones du canal {LIBELLES_CANAL[v.canal]} se verront proposer cette version
        {v.obligatoire ? ' de façon OBLIGATOIRE (application bloquée tant qu\'elle n\'est pas installée)' : ''}. La version active précédente de ce canal sera retirée.
      </BandeauInfo>
      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 12px', fontSize: 13, margin: '0 0 12px' }}>
        <dt>Canal</dt><dd style={{ margin: 0 }}>{LIBELLES_CANAL[v.canal]}</dd>
        <dt>Version</dt><dd style={{ margin: 0 }}>{v.versionName} (code {v.versionCode})</dd>
        <dt>Version minimale</dt><dd style={{ margin: 0 }}>{v.minVersionCode}</dd>
        <dt>Obligatoire</dt><dd style={{ margin: 0 }}>{v.obligatoire ? 'Oui, pour tous' : 'Non (recommandée)'}</dd>
        <dt>Fichier</dt><dd style={{ margin: 0, wordBreak: 'break-all' }}>{v.fichier} · {taille(v.taille)}</dd>
        <dt>Empreinte</dt><dd style={{ margin: 0, wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>{v.sha256}</dd>
        <dt>Lu dans le fichier</dt>
        <dd style={{ margin: 0 }}>
          {v.apk ? <>Application {v.apk.paquet} · version {v.apk.versionName} (code {v.apk.versionCode}) · signature {v.apk.signature} vérifiée
            <span style={{ display: 'block' }}>{v.apk.certificat_attendu ? <Badge ton="succes">Clé de signature SOGAS Employé</Badge> : <Badge ton="danger">Clé de signature inattendue</Badge>}</span>
            <span style={{ display: 'block', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: 12 }}>certificat {v.apk.certificat_sha256}</span></> : '—'}
        </dd>
        <dt>Préparée par</dt><dd style={{ margin: 0 }}>{v.prepare_par?.nom || 'Non renseigné'} · {dateHeure(v.prepare_le)}</dd>
        <dt>Notes</dt><dd style={{ margin: 0 }}>{v.notes || '—'}</dd>
      </dl>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={verifie} onChange={(e) => setVerifie(e.target.checked)} />
        J'ai vérifié la version, les notes et le caractère obligatoire ; cette version a été essayée sur un téléphone.
      </label>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={CheckCircle2} disabled={!verifie || envoi || (v.apk && !v.apk.certificat_attendu)} onClick={valider}>Valider et activer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

function FenetreRetirer({ version, onFermer, onRetiree }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  const active = version.statut?.code === 'VALIDEE';
  const retirer = async () => {
    try { await axios.post(`${API}/${version.id}/retirer`, { motif: motif.trim() }); onRetiree(); } catch (e) { setErreur(messageErreur(e)); }
  };
  return (
    <Fenetre titre={`Retirer la version ${version.versionName}`} Icone={Ban} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 10px', fontSize: 13 }}>
        {active
          ? 'Les téléphones ne se verront plus proposer cette version (ceux qui l\'ont installée la gardent). Aucune autre version ne devient active automatiquement. Définitif.'
          : 'Cette version préparée ne pourra plus être validée. Définitif.'}
      </p>
      <Champ libelle="Motif" requis aide="Par exemple : anomalie constatée en recette, mauvais fichier, version remplacée.">
        <textarea className="rh-champ-saisie" rows={2} maxLength={500} value={motif} onChange={(e) => setMotif(e.target.value)} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="danger" Icone={Ban} disabled={motif.trim().length < 5} onClick={retirer}>Retirer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

function LigneVersion({ v, onValider, onRetirer }) {
  const alerteFichier = v.etat_fichier ? LIBELLES_FICHIER[v.etat_fichier] : null;
  return (
    <tr>
      <td className="rh-cellule-principale">
        {v.versionName} <span className="rh-cellule-secondaire">code {v.versionCode}</span>
        {v.obligatoire && <span style={{ display: 'block' }}><Badge ton="danger">Obligatoire</Badge></span>}
        {v.minVersionCode > 0 && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>minimum : code {v.minVersionCode}</span>}
      </td>
      <td>{LIBELLES_CANAL[v.canal] || v.canal}</td>
      <td>
        <Badge ton={TONS_STATUT[v.statut?.code] || 'neutre'} point>{v.statut?.libelle}</Badge>
        {v.statut?.code === 'RETIREE' && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>le {dateHeure(v.retiree_le)}{v.retiree_par?.nom ? ` par ${v.retiree_par.nom}` : ''} · {v.motif_retrait}</span>}
      </td>
      <td style={{ wordBreak: 'break-all' }}>
        {v.fichier}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{taille(v.taille)}</span>
        {alerteFichier && <span style={{ display: 'block' }}><Badge ton="danger">{alerteFichier}</Badge></span>}
      </td>
      <td>{v.prepare_par?.nom || '—'}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{dateHeure(v.prepare_le)}</span></td>
      <td>{v.valide_par ? <>{v.valide_par.nom}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{dateHeure(v.valide_le)}</span></> : '—'}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        {v.statut?.code === 'BROUILLON' && (v.peut_valider
          ? <Bouton petit variante="primaire" Icone={ShieldCheck} onClick={() => onValider(v)}>Valider</Bouton>
          : <span className="rh-cellule-secondaire" title="Vous avez préparé cette version : une autre personne doit la valider.">À valider par une autre personne</span>)}
        {v.peut_retirer && <Bouton petit variante="discret" Icone={Ban} onClick={() => onRetirer(v)}>Retirer</Bouton>}
      </td>
    </tr>
  );
}

export default function VersionsMobilePage() {
  const [droits, setDroits] = useState(null);
  const [canal, setCanal] = useState('');
  const [versions, setVersions] = useState(null);
  const [fichiers, setFichiers] = useState([]);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const peut = !!droits?.gerer;

  useEffect(() => {
    axios.get(`${API}/droits`).then((r) => setDroits(r.data.data || { gerer: false })).catch(() => setDroits({ gerer: false }));
  }, []);
  const charger = useCallback(async () => {
    setErreur('');
    try {
      const [v, f] = await Promise.all([
        axios.get(API, { params: { canal: canal || undefined } }),
        axios.get(`${API}/fichiers`),
      ]);
      setVersions(toArray(v.data.data?.versions));
      setFichiers(toArray(f.data.data?.fichiers));
    } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); setVersions([]); }
  }, [canal]);
  useEffect(() => { if (peut) charger(); }, [peut, charger]);

  const fermerPuis = (texte) => { setFenetre(null); setMessage(texte); charger(); };

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Paramètres', vers: '/parametres' }, { libelle: 'Application mobile' }]} titre="Application mobile SOGAS Employé"
          sousTitre="Versions proposées aux téléphones, par canal. Une version est préparée par une personne et validée par une autre : c'est la validation qui l'active."
          actions={peut && <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ genre: 'preparer' })}>Préparer une version</Bouton>} />
        {!droits && <Squelette largeur="50%" />}
        {droits && !peut && <EtatVide Icone={Smartphone} titre="Accès non autorisé" texte="La gestion des versions demande l'habilitation « Gérer les versions de l'application mobile »." />}
        {peut && (
          <>
            {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
            {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
            <div className="rh-outils">
              <div style={{ minWidth: 200 }}>
                <Selecteur libelle="Canal" valeur={canal} onChange={setCanal} tous="Tous" options={[{ valeur: 'recette', libelle: 'Recette' }, { valeur: 'officiel', libelle: 'Officiel' }]} />
              </div>
            </div>
            <Carte titre="Versions" Icone={Smartphone}>
              {!versions ? <Squelette largeur="60%" /> : !versions.length ? <EtatVide Icone={Smartphone} titre="Aucune version" texte="Déposez un APK sur le serveur, puis préparez la première version." /> : (
                <div className="rh-conteneur-tableau">
                  <table className="rh-tableau">
                    <thead><tr><th>Version</th><th>Canal</th><th>État</th><th>Fichier</th><th>Préparée par</th><th>Validée par</th><th aria-label="Actions" /></tr></thead>
                    <tbody>
                      {versions.map((v) => <LigneVersion key={v.id} v={v} onValider={(x) => setFenetre({ genre: 'valider', version: x })} onRetirer={(x) => setFenetre({ genre: 'retirer', version: x })} />)}
                    </tbody>
                  </table>
                </div>
              )}
            </Carte>
          </>
        )}
        {fenetre?.genre === 'preparer' && <FenetrePreparer fichiers={fichiers} onFermer={() => setFenetre(null)} onPreparee={(v) => fermerPuis(`Version ${v.versionName} préparée. Elle doit maintenant être validée par une autre personne.`)} />}
        {fenetre?.genre === 'valider' && <FenetreValider version={fenetre.version} onFermer={() => setFenetre(null)} onValidee={() => fermerPuis(`Version ${fenetre.version.versionName} validée : elle est proposée aux téléphones du canal ${LIBELLES_CANAL[fenetre.version.canal]}.`)} />}
        {fenetre?.genre === 'retirer' && <FenetreRetirer version={fenetre.version} onFermer={() => setFenetre(null)} onRetiree={() => fermerPuis(`Version ${fenetre.version.versionName} retirée.`)} />}
      </div>
    </Layout>
  );
}
